import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import TenantManager from './components/TenantManager';
import ProjectManager from './components/ProjectManager';
import AccessControlPage from './components/AccessControlPage';
import AuditLogViewer from './components/AuditLogViewer';
import PortalAdminManagement from './components/PortalAdminManagement';
import GusWeeklyRecipients from './components/GusWeeklyRecipients';
import ServiceConfigStatus from './components/ServiceConfigStatus';
import VersionManager from './components/VersionManager';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { GUSThemeProvider, useTheme } from './contexts/ThemeContext';

// MUI theme factory - creates theme based on current mode
const createMuiTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#32828B', // SilaTeal - GUS brand color
      light: '#6fa7ad',
      dark: '#2d757d',
    },
    secondary: {
      main: '#41A35F', // SilaGreen - accent
      light: '#67b57f',
      dark: '#3a9255',
    },
    success: {
      main: '#41A35F',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: mode === 'light' ? '#dc2626' : '#ef4444',
    },
    background: {
      default: mode === 'light' ? '#f8f9fa' : '#0a0a0a',
      paper: mode === 'light' ? '#ffffff' : '#1a1a1a',
    },
    text: {
      primary: mode === 'light' ? '#0f172a' : '#ffffff',
      secondary: mode === 'light' ? '#374151' : '#94a3b8',
    },
  },
  typography: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.5px',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.3px',
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '0.9375rem', // 15px
    },
    body2: {
      fontSize: '0.8125rem', // 13px
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          fontFamily: "'DM Sans', -apple-system, sans-serif",
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(50, 130, 139, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.1)'
            : '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontFamily: "'DM Sans', sans-serif",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backdropFilter: 'blur(16px)',
          background: mode === 'light'
            ? 'rgba(255, 255, 255, 0.95)'
            : 'rgba(26, 26, 26, 0.95)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: "'DM Sans', sans-serif",
        },
      },
    },
  },
});

// Theme-aware MUI wrapper that uses our theme context
const ThemeAwareMuiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  const muiTheme = React.useMemo(() => createMuiTheme(theme), [theme]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/organizations" element={<TenantManager />} />
        <Route path="/projects" element={<ProjectManager />} />
        <Route path="/access-control" element={<AccessControlPage />} />
        {/* Legacy routes - redirect to unified Access Control page */}
        <Route path="/users" element={<Navigate to="/access-control" replace />} />
        <Route path="/groups" element={<Navigate to="/access-control" replace />} />
        <Route path="/audit" element={<AuditLogViewer />} />
        <Route path="/portal-admins" element={<PortalAdminManagement />} />
        <Route path="/gus-weekly" element={<GusWeeklyRecipients />} />
        <Route path="/services" element={<ServiceConfigStatus />} />
        <Route path="/versions" element={<VersionManager />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <GUSThemeProvider defaultTheme="light">
      <ThemeAwareMuiProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <AppRoutes />
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeAwareMuiProvider>
    </GUSThemeProvider>
  );
}

export default App;
